import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { API, useAuth } from "../App";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import {
  FV_BODY,
  FV_CARD,
  FV_CHIP_NEUTRAL,
  FV_PAGE_TITLE,
  FV_STEP_KICKER,
  FV_SECTION_TITLE,
  FV_INPUT,
  FV_BTN_PRIMARY,
  FV_META,
  FV_HELP,
} from "../styles/fieldVisitUi";

const STATUS_LABELS = {
  scheduled: "Scheduled",
  capturing: "Capturing",
  complete: "Complete",
};

const FieldVisitMissionBoardPage = () => {
  const { user } = useAuth();
  const navigate = useNavigate();

  const [missions, setMissions] = useState([]);
  const [loading, setLoading] = useState(false);
  const [startingId, setStartingId] = useState(null);
  const [showCompleted, setShowCompleted] = useState(false);
  const [errorMsg, setErrorMsg] = useState(null);
  const [dateRange, setDateRange] = useState(() => {
    const today = new Date();
    const start = new Date(today);
    start.setDate(today.getDate() - 1);
    const end = new Date(today);
    end.setDate(today.getDate() + 7);
    return {
      start: start.toISOString().slice(0, 10),
      end: end.toISOString().slice(0, 10),
    };
  });

  useEffect(() => {
    const run = async () => {
      const { start, end } = dateRange;
      if (!start || !end) return;
      setLoading(true);
      try {
        const startIso = `${start}T00:00:00`;
        const endIso = `${end}T23:59:59`;
        const res = await API.get("/appointments", { params: { start: startIso, end: endIso } });
        const data = Array.isArray(res.data) ? res.data : [];
        const mapped = data.map((appt) => {
          const fv = appt.field_visit_summary || null;
          let status = "scheduled";
          if (fv?.status === "draft") status = "capturing";
          if (fv?.status === "complete") status = "complete";
          return {
            appointment_id: appt.appointment_id,
            client_name: appt.lead_contact_name || appt.lead_label || "Client",
            address: appt.location_address || appt.lead_service_address || "",
            when: appt.scheduled_start,
            job_type: appt.title || "",
            field_visit: fv,
            status,
          };
        });
        setMissions(mapped);
      } catch (e) {
        // non-fatal
      } finally {
        setLoading(false);
      }
    };
    run();
  }, [dateRange]);

  const visible = useMemo(
    () =>
      missions.filter((m) => {
        if (!showCompleted && m.status === "complete") return false;
        return true;
      }),
    [missions, showCompleted]
  );

  const handleAction = async (mission) => {
    setErrorMsg(null);
    const fv = mission.field_visit;
    // If a field visit already exists for this appointment, always open it (any status)
    // Navigate to new field visit page with appointment ID
    if (fv && fv.field_visit_id) {
      navigate(`/field-visit/appointment/${mission.appointment_id}`);
      return;
    }
    if (startingId) return;
    setStartingId(mission.appointment_id);
    try {
      const res = await API.post(`/field-visits/appointments/${mission.appointment_id}/field-visit/start`);
      const body = res.data || res;

      // Refresh this mission row locally so status updates on the board
      setMissions((prev) =>
        prev.map((m) => {
          if (m.appointment_id !== mission.appointment_id) return m;
          return {
            ...m,
            field_visit: body,
            status: body.status === "complete" ? "complete" : "capturing",
          };
        }),
      );

      navigate(`/field-visit/appointment/${mission.appointment_id}`);
    } catch (e) {
      const detail = e?.response?.data?.detail || "Failed to start field visit";
      setErrorMsg(detail);
    } finally {
      setStartingId(null);
    }
  };

  return (
    <div className="p-6 space-y-4 text-slate-100">
      {errorMsg && (
        <div className="text-xs text-red-400 bg-red-950/40 border border-red-700 rounded px-3 py-2">
          {errorMsg}
        </div>
      )}

      <div className="flex items-center justify-between">
        <div>
          <h1 className={`flex items-center gap-2 ${FV_PAGE_TITLE}`}>
            FIELD VISIT  MISSION BOARD
          </h1>
          <p className={FV_BODY + " text-slate-400"}>
            Active field missions for estimators and project managers.
          </p>
        </div>
        <div className="flex items-center gap-2 text-xs text-slate-400">
          <span className={FV_META}>Range</span>
          <Input
            type="date"
            value={dateRange.start}
            onChange={(e) => setDateRange((prev) => ({ ...prev, start: e.target.value }))}
            className={`h-8 w-[140px] text-xs ${FV_INPUT}`}
          />
          <Input
            type="date"
            value={dateRange.end}
            onChange={(e) => setDateRange((prev) => ({ ...prev, end: e.target.value }))}
            className={`h-8 w-[140px] text-xs ${FV_INPUT}`}
          />
          <label className="flex items-center gap-1 ml-4 text-xs text-slate-400">
            <input
              type="checkbox"
              className="accent-emerald-500"
              checked={showCompleted}
              onChange={(e) => setShowCompleted(e.target.checked)}
            />
            Show completed
          </label>
        </div>
      </div>

      <Card className={FV_CARD + " mt-4"}>
        <CardHeader className="flex items-center justify-between">
          <CardTitle className={FV_SECTION_TITLE}>Active Missions</CardTitle>
        </CardHeader>
        <CardContent>
          {loading && (
            <p className={FV_HELP}>Loading missions...</p>
          )}
          {!loading && visible.length === 0 && (
            <p className={FV_META}>No active missions in this range.</p>
          )}
          {!loading && visible.length > 0 && (
            <div className="space-y-2">
              {visible.map((m) => (
                <div
                  key={m.appointment_id}
                  className={`flex items-center justify-between px-3 py-2 text-xs ${FV_CARD}`}
                >
                  <div className="space-y-0.5">
                    <div className="flex items-center gap-2">
                      <span className="font-medium text-slate-100">{m.client_name}</span>
                      {m.job_type && (
                        <span className={FV_META}>{m.job_type}</span>
                      )}
                    </div>
                    {m.address && (
                      <p className={`${FV_HELP} truncate`}>{m.address}</p>
                    )}
                    {m.when && (
                      <p className={FV_META}>{new Date(m.when).toLocaleString()}</p>
                    )}
                  </div>
                  <div className="flex items-center gap-3">
                    <span
                      className={`${FV_STEP_KICKER} ${FV_CHIP_NEUTRAL} border px-2 py-0.5 rounded-full`}
                    >
                      {STATUS_LABELS[m.status] || m.status}
                    </span>
                    <Button
                      size="sm"
                      className={`h-8 px-3 text-xs disabled:opacity-60 ${FV_BTN_PRIMARY}`}
                      disabled={!!startingId && startingId !== m.appointment_id}
                      onClick={() => handleAction(m)}
                    >
                      {m.field_visit?.status === "complete"
                        ? "View"
                        : m.field_visit
                        ? "Resume Site Visit"
                        : startingId === m.appointment_id
                        ? "Starting..."
                        : "Start Site Visit"}
                    </Button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default FieldVisitMissionBoardPage;
